import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Lock, Save, Eye, EyeOff, ShieldCheck } from "lucide-react";

export default function AdminSettingsPage() {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(false);

  const onChange = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    if (form.new_password !== form.confirm) {
      toast.error("New password and confirmation don't match.");
      return;
    }
    if (form.new_password === form.current_password) {
      toast.error("New password must be different from the current one.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/admin/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Password updated. Use the new one on your next login.");
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 md:p-8" data-testid="admin-settings">
      <div className="mb-6">
        <span className="text-xs font-semibold tracking-[0.18em] uppercase text-[#0F5E48]">
          Settings
        </span>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-[#0A1628] mt-1.5">
          Account & security
        </h1>
      </div>

      <div className="max-w-xl">
        <form
          onSubmit={submit}
          data-testid="admin-change-password-form"
          className="bg-white border border-[#E1D8C6] rounded-2xl p-6 md:p-7 soft-shadow space-y-4"
        >
          <div className="flex items-center gap-2 text-[#0F1419]">
            <div className="w-9 h-9 rounded-xl bg-[#0F5E48]/10 text-[#0F5E48] flex items-center justify-center">
              <Lock className="w-4 h-4" />
            </div>
            <div>
              <h2 className="font-display font-semibold text-lg leading-tight">Change password</h2>
              <p className="text-xs text-[#777E89]">Used to sign into the admin panel.</p>
            </div>
          </div>

          <Field label="Current password">
            <div className="relative">
              <input
                data-testid="settings-current-password"
                type={showCurrent ? "text" : "password"}
                value={form.current_password}
                onChange={onChange("current_password")}
                required
                autoComplete="current-password"
                placeholder="••••••••"
                className="inp pr-16"
              />
              <button type="button" onClick={() => setShowCurrent((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#777E89] hover:text-[#0F1419] inline-flex items-center gap-1">
                {showCurrent ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                {showCurrent ? "Hide" : "Show"}
              </button>
            </div>
          </Field>

          <Field label="New password" hint="Minimum 8 characters. Mix letters, numbers, and a symbol.">
            <div className="relative">
              <input
                data-testid="settings-new-password"
                type={showNew ? "text" : "password"}
                value={form.new_password}
                onChange={onChange("new_password")}
                required
                minLength={8}
                autoComplete="new-password"
                placeholder="••••••••"
                className="inp pr-16"
              />
              <button type="button" onClick={() => setShowNew((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#777E89] hover:text-[#0F1419] inline-flex items-center gap-1">
                {showNew ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                {showNew ? "Hide" : "Show"}
              </button>
            </div>
          </Field>

          <Field label="Confirm new password">
            <input
              data-testid="settings-confirm-password"
              type={showNew ? "text" : "password"}
              value={form.confirm}
              onChange={onChange("confirm")}
              required
              minLength={8}
              autoComplete="new-password"
              placeholder="••••••••"
              className="inp"
            />
          </Field>

          <button
            type="submit"
            disabled={loading}
            data-testid="settings-save-password"
            className="w-full h-12 rounded-lg bg-[#0F5E48] text-white font-semibold hover:bg-[#0B4A38] disabled:opacity-50 inline-flex items-center justify-center gap-2 transition-colors"
          >
            <Save className="w-4 h-4" /> {loading ? "Saving…" : "Save new password"}
          </button>

          <p className="text-xs text-[#777E89] inline-flex items-center gap-1.5 pt-1">
            <ShieldCheck className="w-3.5 h-3.5 text-[#0F5E48]" />
            Every password change is logged to the audit trail.
          </p>
        </form>
      </div>

      <style>{`
        .inp {
          width: 100%;
          height: 48px;
          padding: 0 16px;
          border-radius: 8px;
          border: 1px solid #E1D8C6;
          background: white;
          color: #0F1419;
          outline: none;
          transition: box-shadow .15s, border-color .15s;
        }
        .inp:focus { box-shadow: 0 0 0 2px #0F5E48; border-color: transparent; }
      `}</style>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold uppercase tracking-wider text-[#4A535E] mb-1.5">
        {label}
      </span>
      {children}
      {hint && <span className="block text-xs text-[#777E89] mt-1.5">{hint}</span>}
    </label>
  );
}
